import React, { useEffect, useState } from 'react';
import { Button } from '@h2/ui';
import { Label } from '@h2/ui';
import { ChevronRight, Grid, List, Eye } from 'lucide-react';
import { cn } from '@h2/ui';
import { useWorkSettingsStore } from '../../../stores/useWorkSettingsStore';
import { useConfigurationStore } from '../../../stores/useConfigurationStore';
import { useTaskTypeStore } from '../../../stores/useTaskTypeStore';
import { JobSection } from './JobSection';
import { DataSheetGrid, Column } from 'react-datasheet-grid';
import 'react-datasheet-grid/dist/style.css';

interface FinalConfirmationStepProps {
  onComplete?: () => void;
  onBack?: () => void;
  title?: string;
  description?: string;
  hideTitle?: boolean;
}

type ViewMode = 'list' | 'spreadsheet';

interface TaskRow {
  id: string;
  jobTitle: string;
  language: string;
  taskType: string;
  taskTitle: string;
  duration: string;
  status: string;
  assignee: string;
  unitPrice: number;
  currency: string;
  order: number;
}

export const FinalConfirmationStep: React.FC<FinalConfirmationStepProps> = ({
  title = "작업 설정 - 최종 확인",
  description = "생성될 Job과 Task를 확인하고 필요시 개별 수정하세요.",
  hideTitle = false
}) => {
  // Configuration 스토어에서 데이터 가져오기
  const { formData, updateField } = useConfigurationStore();
  
  // TaskType 스토어에서 이름 가져오기
  const { taskTypes, getTaskTypeById } = useTaskTypeStore();
  
  // 언어 설정 데이터
  const languageData = formData.languageSettingsComponent || { targetLanguages: [], tags: [], memo: '' };
  const targetLanguages = languageData.targetLanguages;
  
  // 최종 확인 데이터
  const finalData = formData.finalConfirmationComponent || { 
    jobs: [], 
    expandedJobs: [], 
    selectedLanguages: [] 
  };
  
  // 작업자 배정 데이터
  const workerAssignmentData = formData.workerAssignmentComponent || {
    assignmentMatrix: [],
    availableWorkers: [],
    availableGroups: []
  };
  
  // WorkSettings 스토어 (UI 상태 관리용)
  const { markStepCompleted } = useWorkSettingsStore();
  
  // 뷰 모드 상태
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  
  const [selectedLanguages, setSelectedLanguages] = React.useState<string[]>(finalData.selectedLanguages.length > 0 ? finalData.selectedLanguages : targetLanguages);
  
  const languageNames: Record<string, string> = {
    'ko': '한국어',
    'en': '영어',
    'ja': '일본어',
    'zh': '중국어',
    'es': '스페인어',
    'hi': '힌디어'
  };

  // TaskType 이름 가져오기 헬퍼 함수
  const getTaskTypeName = (taskTypeId: string): string => {
    const taskType = getTaskTypeById(taskTypeId);
    return taskType?.name || taskTypeId;
  };

  // 작업자 배정 정보 가져오기 헬퍼 함수
  const getTaskAssignment = (taskTypeId: string, language: string) => {
    if (!workerAssignmentData.assignmentMatrix) return null;
    
    // assignmentMatrix에서 해당 task type과 언어에 맞는 배정 찾기
    const assignment = workerAssignmentData.assignmentMatrix.find(item => 
      item.taskType === taskTypeId && item.language === language
    );
    
    return assignment || null;
  };
  
  // Job 생성 함수
  const generateJobs = () => {
    const workflowData = formData.workflowComponent || { taskNodes: [], connections: [] };
    const taskNodes = workflowData.taskNodes;
    
    console.log('🎯 FinalConfirmationStep generateJobs:', {
      targetLanguages,
      taskNodes,
      workflowData,
      finalData
    });
    
    if (targetLanguages.length === 0 || taskNodes.length === 0) {
      console.log('⚠️ Job 생성 실패 - 언어 또는 Task 없음');
      return;
    }
    
    const newJobs = targetLanguages.map((language, index) => ({
      id: `job_${language}_${Date.now()}_${index}`,
      title: `${languageNames[language]} 작업`,
      language: language,
      status: 'pending' as const,
      tasks: taskNodes.map((taskNode, taskIndex) => {
        const assignment = getTaskAssignment(taskNode.type, language);
        const taskTypeName = getTaskTypeName(taskNode.type);
        
        return {
          id: `task_${taskNode.id}_${language}_${taskIndex}`,
          type: taskTypeName, // TaskType 이름 사용
          title: `${taskTypeName} - ${languageNames[language]}`,
          duration: taskNode.duration,
          status: 'pending' as const,
          assignee: assignment ? {
            type: assignment.assigneeType,
            id: assignment.assigneeId,
            name: assignment.assigneeName
          } : null,
          unitPrice: assignment?.unitPrice || 0,
          currency: assignment?.currency || 'KRW',
          order: taskNode.order
        };
      })
    }));
    
    console.log('✅ 생성된 Job들:', newJobs);
    
    // Configuration Store에 생성된 Job 저장
    updateField('finalConfirmationComponent', {
      ...finalData,
      jobs: newJobs,
      selectedLanguages: targetLanguages
    });
  };

  useEffect(() => {
    generateJobs();
  }, [targetLanguages, formData.workflowComponent]);
  
  const toggleLanguageFilter = (language: string) => {
    setSelectedLanguages(prev => 
      prev.includes(language) 
        ? prev.filter(l => l !== language)
        : [...prev, language]
    );
  };
  
  const toggleAllLanguages = () => {
    setSelectedLanguages(prev => 
      prev.length === targetLanguages.length ? [] : [...targetLanguages]
    );
  };
  
  // Job 확장/축소 함수들
  const toggleJobExpand = (jobId: string) => {
    const currentExpanded = finalData.expandedJobs || [];
    const newExpanded = currentExpanded.includes(jobId)
      ? currentExpanded.filter(id => id !== jobId)
      : [...currentExpanded, jobId];
    
    updateField('finalConfirmationComponent', {
      ...finalData,
      expandedJobs: newExpanded
    });
  };

  const expandAllJobs = () => {
    const allJobIds = finalData.jobs.map(job => job.id);
    updateField('finalConfirmationComponent', {
      ...finalData,
      expandedJobs: allJobIds
    });
  };
  
  const collapseAllJobs = () => {
    updateField('finalConfirmationComponent', {
      ...finalData,
      expandedJobs: []
    });
  };
  
  const filteredJobs = finalData.jobs.filter(job => 
    selectedLanguages.includes(job.language)
  );
  
  const totalJobs = filteredJobs.length;
  const totalTasks = filteredJobs.reduce((sum, job) => sum + job.tasks.length, 0);
  
  // 통화별 총 비용 계산
  const totalCostByCurrency = filteredJobs.reduce((acc, job) => {
    job.tasks.forEach(task => {
      if (!acc[task.currency]) {
        acc[task.currency] = 0;
      }
      acc[task.currency] += task.unitPrice;
    });
    return acc;
  }, {} as Record<string, number>);
  
  const formatCurrency = (amount: number, currency: string): string => {
    const symbols: Record<string, string> = {
      'KRW': '₩',
      'USD': '$',
      'JPY': '¥',
      'CNY': '¥',
      'EUR': '€',
      'INR': '₹'
    };
    return `${symbols[currency] || currency}${amount.toLocaleString()}`;
  };

  // 스프레드시트용 데이터 변환
  const convertToSpreadsheetData = (): TaskRow[] => {
    const rows: TaskRow[] = [];
    
    // Job을 언어 순서로 정렬 (targetLanguages 배열 순서 따름)
    const sortedJobs = [...filteredJobs].sort((a, b) => {
      const aIndex = targetLanguages.indexOf(a.language);
      const bIndex = targetLanguages.indexOf(b.language);
      return aIndex - bIndex;
    });
    
    sortedJobs.forEach(job => {
      // Task를 작업 순서(order)로 정렬
      const sortedTasks = [...job.tasks].sort((a, b) => a.order - b.order);
      
      sortedTasks.forEach(task => {
        rows.push({
          id: task.id,
          jobTitle: job.title,
          language: languageNames[job.language] || job.language,
          taskType: task.type, // 이미 TaskType 이름이 들어있음
          taskTitle: task.title,
          duration: `${task.duration.value}${task.duration.unit === 'hour' ? 'H' : 'D'}`,
          status: task.status === 'pending' ? '대기중' : task.status,
          assignee: task.assignee?.name || '미배정',
          unitPrice: task.unitPrice,
          currency: task.currency,
          order: task.order
        });
      });
    });
    
    return rows;
  };

  // 스프레드시트 데이터 업데이트
  const updateSpreadsheetData = (newRows: TaskRow[]) => {
    const updatedJobs = finalData.jobs.map(job => {
      const updatedTasks = job.tasks.map(task => {
        const row = newRows.find(r => r.id === task.id);
        if (row) {
          // duration 파싱
          const durationMatch = row.duration.match(/(\d+)([HD])/);
          const duration = durationMatch 
            ? { 
                value: parseInt(durationMatch[1]), 
                unit: durationMatch[2] === 'H' ? 'hour' as const : 'day' as const 
              }
            : task.duration;

          return {
            ...task,
            title: row.taskTitle,
            duration,
            unitPrice: row.unitPrice,
            currency: row.currency
          };
        }
        return task;
      });
      return { ...job, tasks: updatedTasks };
    });

    updateField('finalConfirmationComponent', {
      ...finalData,
      jobs: updatedJobs
    });
  };

  // 스프레드시트 컬럼 정의
  const columns: Column<TaskRow>[] = [
    {
      key: 'jobTitle',
      name: 'Job',
      disabled: true,
      width: 150
    },
    {
      key: 'language',
      name: '언어',
      disabled: true,
      width: 80
    },
    {
      key: 'order',
      name: '순서',
      disabled: true,
      width: 60
    },
    {
      key: 'taskType',
      name: '작업유형',
      disabled: true,
      width: 100
    },
    {
      key: 'taskTitle',
      name: '작업명',
      width: 200
    },
    {
      key: 'duration',
      name: '기간',
      width: 80
    },
    {
      key: 'assignee',
      name: '배정',
      disabled: true,
      width: 100
    },
    {
      key: 'unitPrice',
      name: '단가',
      width: 100
    },
    {
      key: 'currency',
      name: '통화',
      width: 60
    },
    {
      key: 'status',
      name: '상태',
      disabled: true,
      width: 80
    }
  ];
  
  return (
    <div className="w-full max-w-none">
      {!hideTitle && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
      )}
      <div className="space-y-4">
        {/* 컨트롤 바 */}
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2">
            <Label>언어 필터:</Label>
            {targetLanguages.map(lang => (
              <Button
                key={lang}
                variant={selectedLanguages.includes(lang) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleLanguageFilter(lang)}
              >
                {selectedLanguages.includes(lang) && '✓ '}
                {languageNames[lang]}
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleAllLanguages}
            >
              전체선택
            </Button>
          </div>
          
          {/* 뷰 모드 토글 */}
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">보기 모드:</Label>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="flex items-center gap-1"
            >
              <List className="w-3 h-3" />
              리스트
            </Button>
            <Button
              variant={viewMode === 'spreadsheet' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('spreadsheet')}
              className="flex items-center gap-1"
            >
              <Grid className="w-3 h-3" />
              스프레드시트
            </Button>
          </div>
        </div>
        
        {/* 컨텐츠 영역 */}
        {viewMode === 'list' ? (
          /* 리스트 모드 */
          <div className="space-y-4">
            {filteredJobs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                선택된 언어에 해당하는 Job이 없습니다.
              </div>
            ) : (
              filteredJobs.map(job => (
                <div key={job.id} className="border rounded-lg bg-card">
                  {/* Job 헤더 (클릭 가능) */}
                  <div 
                    className="flex items-center justify-between p-4 border-b bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
                    onClick={() => toggleJobExpand(job.id)}
                  >
                    <div className="flex items-center gap-3">
                      <ChevronRight 
                        className={cn(
                          "w-4 h-4 transition-transform",
                          finalData.expandedJobs.includes(job.id) ? "rotate-90" : ""
                        )}
                      />
                      <div className="font-medium text-lg">{job.title}</div>
                      <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                        {languageNames[job.language]}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {job.tasks.length}개 작업
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span>✅ 배정완료 {job.tasks.filter(t => t.assignee).length}개</span>
                      <span>예상비용: {formatCurrency(
                        job.tasks.reduce((sum, task) => sum + task.unitPrice, 0),
                        job.tasks[0]?.currency || 'KRW'
                      )}</span>
                    </div>
                  </div>
                  
                  {/* Task 수평 배치 (폴딩 가능) */}
                  {finalData.expandedJobs.includes(job.id) && (
                    <div className="p-4">
                      <div className="flex gap-4 overflow-x-auto">
                        {job.tasks
                          .sort((a, b) => a.order - b.order)
                          .map((task, index) => (
                            <div key={task.id} className="flex-shrink-0 w-64 border rounded-lg p-3 bg-muted/30">
                              {/* Task 헤더 */}
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-medium">
                                  {task.order}
                                </div>
                                <div className="font-medium text-sm">{task.type}</div>
                              </div>
                              
                              {/* Task 정보 */}
                              <div className="space-y-2 text-sm">
                                {/* 배정 */}
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">배정:</span>
                                  {task.assignee ? (
                                    <div className="flex items-center gap-1">
                                      {task.assignee.type === 'worker' ? '👤' : 
                                       task.assignee.type === 'group' ? '👥' :
                                       task.assignee.type === 'ai' ? '🤖' :
                                       task.assignee.type === 'pm' ? '👔' : 
                                       task.assignee.type === 'auto' ? '⚙️' : '👤'}
                                      <span>{task.assignee.name}</span>
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">미배정</span>
                                  )}
                                </div>
                                
                                {/* 기간 */}
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">기간:</span>
                                  <span>📅 {task.duration.value}{task.duration.unit === 'hour' ? 'H' : 'D'}</span>
                                </div>
                                
                                {/* 단가 */}
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">단가:</span>
                                  <span className="font-medium">
                                    {task.unitPrice > 0 
                                      ? formatCurrency(task.unitPrice, task.currency)
                                      : '미설정'
                                    }
                                  </span>
                                </div>
                                
                                {/* 상태 */}
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">상태:</span>
                                  <div className={cn(
                                    "px-2 py-1 rounded text-xs",
                                    task.status === 'pending' 
                                      ? "bg-yellow-100 text-yellow-800" 
                                      : "bg-green-100 text-green-800"
                                  )}>
                                    {task.status === 'pending' ? '대기중' : task.status}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          /* 스프레드시트 모드 */
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                <Eye className="w-4 h-4 inline mr-1" />
                스프레드시트 모드에서 작업명, 기간, 단가, 통화를 편집할 수 있습니다.
              </div>
              <div className="text-sm text-muted-foreground">
                총 {convertToSpreadsheetData().length}개 작업
              </div>
            </div>
            
            {filteredJobs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                선택된 언어에 해당하는 Job이 없습니다.
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden bg-white">
                <DataSheetGrid
                  value={convertToSpreadsheetData()}
                  onChange={updateSpreadsheetData}
                  columns={columns}
                  height={Math.min(600, convertToSpreadsheetData().length * 40 + 100)}
                  rowClassName="text-sm"
                  addRowsComponent={false}
                  duplicateRowsComponent={false}
                  deleteRowsComponent={false}
                />
              </div>
            )}
          </div>
        )}

        {/* 스텝 5 (작업자 배정) 정보 요약 */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-md font-medium text-blue-900 mb-3 flex items-center gap-2">
            <Eye className="w-4 h-4" />
            작업자 배정 정보 (스텝 5)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="space-y-2">
              <div className="font-medium text-blue-800">배정 현황</div>
              <div className="space-y-1 text-blue-700">
                <div>• 총 작업: {totalTasks}개</div>
                <div>• 배정 완료: {filteredJobs.reduce((sum, job) => sum + job.tasks.filter(task => task.assignee).length, 0)}개</div>
                <div>• 미배정: {filteredJobs.reduce((sum, job) => sum + job.tasks.filter(task => !task.assignee).length, 0)}개</div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="font-medium text-blue-800">언어별 배정</div>
              <div className="space-y-1 text-blue-700">
                {targetLanguages.map(lang => {
                  const langJobs = filteredJobs.filter(job => job.language === lang);
                  const langTasks = langJobs.reduce((sum, job) => sum + job.tasks.length, 0);
                  const assignedTasks = langJobs.reduce((sum, job) => sum + job.tasks.filter(task => task.assignee).length, 0);
                  return (
                    <div key={lang}>• {languageNames[lang]}: {assignedTasks}/{langTasks}</div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <div className="font-medium text-blue-800">예상 비용</div>
              <div className="space-y-1 text-blue-700">
                {Object.entries(totalCostByCurrency).map(([currency, amount]) => (
                  <div key={currency}>• {formatCurrency(amount, currency)}</div>
                ))}
                {Object.keys(totalCostByCurrency).length === 0 && (
                  <div>• 단가 설정 필요</div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* 통계 */}
        <div className="flex items-center justify-center gap-6 p-4 bg-muted rounded-lg text-sm">
          <div>📊 총 Job: {totalJobs}개</div>
          <div>총 Task: {totalTasks}개</div>
          <div>
            예상 비용: {Object.entries(totalCostByCurrency).map(([currency, amount]) => (
              <span key={currency} className="ml-2">
                {formatCurrency(amount, currency)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};